import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import apiClient from '../services/authService';
import { executeRecaptcha } from '../services/recaptcha';
import {
  Clock,
  Zap,
  Shield,
  TrendingUp,
  Users,
  Bell,
  CheckCircle,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';

const PreLaunchPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1); // 1: email, 2: details
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    portfolioSize: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (submitMessage.text) setSubmitMessage({ type: '', text: '' });
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (formData.email) {
      setCurrentStep(2);
    }
  };

  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage({ type: '', text: '' });

    try {
      const recaptchaToken = await executeRecaptcha('lead_form');
      const response = await apiClient.post('/api/leads', {
        ...formData,
        recaptchaToken,
        source: 'pre-launch-page'
      });

      if (response.data.success) {
        setSubmitted(true);
        setSubmitMessage({
          type: 'success',
          text: response.data.message || 'Thank you! We\'ll be in touch soon.'
        });
      } else {
        setSubmitMessage({
          type: 'error',
          text: response.data.message || 'Something went wrong. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error submitting waitlist:', error);
      setSubmitMessage({
        type: 'error',
        text: error.response?.data?.message || 'Network error. Please check your connection and try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
    setSubmitMessage({ type: '', text: '' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-gray-50 to-white">
      {/* Navigation */}
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <img src="/nivasapro.ai-app-Icon.png" alt="NivasaPro" className="h-8 w-8" />
            <span className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              NivasaPro.ai
            </span>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-gray-600 hover:text-cyan-600"
          >
            Back to Home
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center mb-16">
          <p className="text-sm font-semibold tracking-wide text-cyan-700 uppercase mb-4">
            Coming Soon: Pre-Launch Phase
          </p>

          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            The Future of Property Management is
            <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent"> Almost Here</span>
          </h1>

          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            We're putting the finishing touches on NivasaPro.ai - your AI-powered property management platform.
            Join our waitlist to get early access when we launch!
          </p>

          {/* Multi-Step Waitlist Form */}
          <div className="max-w-xl mx-auto">
            {!submitted ? (
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
                {/* Step Indicator */}
                <div className="flex items-center justify-center mb-8">
                  <div className="flex items-center">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                      currentStep >= 1 ? 'bg-cyan-600 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {currentStep > 1 ? <CheckCircle className="h-5 w-5" /> : '1'}
                    </div>
                    <div className={`w-16 h-1 mx-2 ${
                      currentStep >= 2 ? 'bg-cyan-600' : 'bg-gray-200'
                    }`}></div>
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
                      currentStep >= 2 ? 'bg-cyan-600 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>
                      2
                    </div>
                  </div>
                </div>

                {/* Step 1: Email */}
                {currentStep === 1 && (
                  <form onSubmit={handleEmailSubmit} className="space-y-6">
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        Join the Waitlist
                      </h3>
                      <p className="text-gray-600">
                        Enter your email to get started
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-gray-700 font-medium mb-2">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="mt-1"
                        autoFocus
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white py-6 text-lg font-semibold group"
                    >
                      Continue
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </Button>

                    <p className="text-xs text-gray-500 text-center">
                      Be among the first to experience AI-powered property management
                    </p>
                  </form>
                )}

                {/* Step 2: Details */}
                {currentStep === 2 && (
                  <form onSubmit={handleFinalSubmit} className="space-y-6">
                    <div className="text-center mb-6">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">
                        Tell Us About You
                      </h3>
                      <p className="text-gray-600">
                        Help us personalize your experience
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="name" className="text-gray-700 font-medium mb-2">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className="mt-1"
                        autoFocus
                      />
                    </div>

                    <div>
                      <Label htmlFor="portfolioSize" className="text-gray-700 font-medium mb-2">
                        Portfolio Size *
                      </Label>
                      <select
                        id="portfolioSize"
                        name="portfolioSize"
                        required
                        value={formData.portfolioSize}
                        onChange={handleChange}
                        className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      >
                        <option value="">Select number of units</option>
                        <option value="1-5">1-5 units</option>
                        <option value="6-20">6-20 units</option>
                        <option value="21+">21+ units</option>
                      </select>
                    </div>

                    {submitMessage.text && (
                      <div className={`p-4 rounded-lg ${
                        submitMessage.type === 'success'
                          ? 'bg-green-50 text-green-800 border border-green-200'
                          : 'bg-red-50 text-red-800 border border-red-200'
                      }`}>
                        {submitMessage.text}
                      </div>
                    )}

                    <div className="flex gap-3">
                      <Button
                        type="button"
                        onClick={handleBack}
                        variant="outline"
                        className="flex-1 py-6 text-lg font-semibold group"
                      >
                        <ArrowLeft className="mr-2 h-5 w-5 group-hover:-translate-x-1 transition-transform" />
                        Back
                      </Button>
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white py-6 text-lg font-semibold"
                      >
                        {isSubmitting ? 'Submitting...' : 'Get Early Access'}
                      </Button>
                    </div>

                    <p className="text-xs text-gray-500 text-center">
                      By submitting, you agree to receive emails from NivasaPro.ai. Unsubscribe anytime.
                    </p>
                  </form>
                )}
              </div>
            ) : (
              // Success State
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200 text-center">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  You're on the list!
                </h3>
                <p className="text-gray-600 mb-6">
                  We'll notify you at <strong>{formData.email}</strong> when we launch.
                  We'll also reach out within 24 hours to schedule your personalized demo.
                </p>
                <Button
                  onClick={() => navigate('/')}
                  className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white"
                >
                  Back to Home
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Features Grid */}
        <div className="max-w-6xl mx-auto mt-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            What to Expect
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="p-6 rounded-xl border border-gray-200 bg-white hover:border-cyan-300 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4">
                <Clock className="h-6 w-6 text-cyan-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">24/7 AI Assistant</h3>
              <p className="text-gray-600">
                Your AI agent handles tenant requests around the clock with instant, professional responses.
              </p>
            </div>

            <div className="p-6 rounded-xl border border-gray-200 bg-white hover:border-cyan-300 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <Zap className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Automated Coordination</h3>
              <p className="text-gray-600">
                Automatically schedule maintenance, coordinate vendors, and keep everyone informed.
              </p>
            </div>

            <div className="p-6 rounded-xl border border-gray-200 bg-white hover:border-cyan-300 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Enterprise Security</h3>
              <p className="text-gray-600">
                Bank-level encryption and SOC 2 compliance designed for property managers.
              </p>
            </div>

            <div className="p-6 rounded-xl border border-gray-200 bg-white hover:border-cyan-300 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Better Returns</h3>
              <p className="text-gray-600">
                Faster responses lead to happier tenants, better retention, and consistent ROI.
              </p>
            </div>

            <div className="p-6 rounded-xl border border-gray-200 bg-white hover:border-cyan-300 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <Users className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Local Expertise</h3>
              <p className="text-gray-600">
                Optimized for Central Ohio with local vendor networks and market knowledge.
              </p>
            </div>

            <div className="p-6 rounded-xl border border-gray-200 bg-white hover:border-cyan-300 hover:shadow-lg transition-all">
              <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center mb-4">
                <Bell className="h-6 w-6 text-pink-600" />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-gray-900">Smart Alerts</h3>
              <p className="text-gray-600">
                Get notified only when needed. AI handles routine tasks while escalating priorities.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="max-w-2xl mx-auto mt-12 text-center">
          <p className="text-gray-600">
            Questions about early access or pilot program?{' '}
            <a href="mailto:hello@nivasapro.ai" className="text-cyan-600 hover:underline font-semibold">
              Contact us at hello@nivasapro.ai
            </a>
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t bg-white mt-16">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <img src="/nivasapro.ai-app-Icon.png" alt="NivasaPro" className="h-6 w-6" />
              <span className="text-xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
                NivasaPro.ai
              </span>
            </div>
            <p className="text-gray-600 text-sm">
              © 2026 NivasaPro.ai. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PreLaunchPage;
